/**
 * Profile page — a reader's personal literary identity: who they are, what
 * they read, and what they contribute to ScribbleReads. Overview assembles
 * every section as a preview; the other tabs show the fuller version of
 * one section at a time. Reads live from Bookshelf/Studio/Communities so
 * the profile is always in sync with those features rather than a fork of
 * their data.
 */

import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AppHeader from '@/components/home/AppHeader';
import { useAuth } from '@/lib/useAuth';
import { useBookshelf } from '@/components/bookshelf';
import { useMyWriting } from '@/components/myWriting';
import { useCommunities } from '@/components/communities';
import { toast } from '@/components/ui/sonner';
import {
  ProfileProvider, useProfile,
  ProfileHeader, ProfileStats, ProfileTabs,
  CurrentlyReading, FavoriteBooks, RecentlyRead, TBRPreview,
  ScribblesSection, WritingSection, CommunitiesSection,
  EditProfileModal,
} from '@/components/profile';
import { PROFILE } from '@/constants/testIds';

function ProfileContent() {
  const { username } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { profile, updateProfile, updatePrivacy, updateBanner, favoriteOrder, reorderFavorites, scribbles, toggleScribbleLike } = useProfile();
  const { books } = useBookshelf();
  const { stories } = useMyWriting();
  const { myCommunities } = useCommunities();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = (key) => {
    setSearchParams(key === 'overview' ? {} : { tab: key }, { replace: false });
  };

  const isOwnProfile = !username || username === user?.handle;

  const readingBooks = useMemo(() => books.filter((b) => b.status === 'reading'), [books]);
  const tbrBooks = useMemo(() => books.filter((b) => b.status === 'tbr'), [books]);
  const finishedBooks = useMemo(
    () => [...books].filter((b) => b.status === 'finished').sort((a, b) => (b.dateFinished ?? 0) - (a.dateFinished ?? 0)),
    [books]
  );
  const favoriteBooks = useMemo(() => {
    const favorites = books.filter((b) => b.isFavorite);
    const byId = new Map(favorites.map((b) => [b.id, b]));
    const ordered = favoriteOrder.map((id) => byId.get(id)).filter(Boolean);
    const remaining = favorites.filter((b) => !favoriteOrder.includes(b.id));
    return [...ordered, ...remaining];
  }, [books, favoriteOrder]);

  const publicStories = useMemo(() => stories.filter((s) => s.visibility === 'published'), [stories]);

  const stats = { booksRead: finishedBooks.length, scribbles: scribbles.length };

  const handleFollowToggle = () => {
    setIsFollowing((prev) => {
      const next = !prev;
      toast(next ? `Following @${profile.handle}` : `Unfollowed @${profile.handle}`);
      return next;
    });
  };

  if (!profile) return null;

  return (
    <div className="relative min-h-screen bg-cream" data-testid={PROFILE.page}>
      <AppHeader />
      <main className="pb-28 md:pb-20">
        <ProfileHeader
          profile={profile}
          stats={stats}
          isOwnProfile={isOwnProfile}
          isFollowing={isFollowing}
          onFollowToggle={handleFollowToggle}
          onEdit={() => setIsEditOpen(true)}
          onUpdateBanner={updateBanner}
        />

        <ProfileStats
          booksRead={stats.booksRead}
          streak={profile.stats?.streak ?? 0}
          scribbles={stats.scribbles}
          tbr={tbrBooks.length}
        />

        <ProfileTabs active={activeTab} onChange={setActiveTab} />

        {activeTab === 'overview' && (
          <>
            <CurrentlyReading books={readingBooks} />
            <FavoriteBooks books={favoriteBooks} isOwnProfile={isOwnProfile} onReorder={reorderFavorites} />
            <RecentlyRead books={finishedBooks.slice(0, 6)} />
            <TBRPreview books={tbrBooks} total={tbrBooks.length} />
            <ScribblesSection
              scribbles={scribbles}
              authorName={profile.name}
              onToggleLike={toggleScribbleLike}
              limit={2}
              onViewAll={() => setActiveTab('scribbles')}
            />
            <WritingSection stories={publicStories} limit={3} />
            <CommunitiesSection communities={myCommunities} limit={4} />
          </>
        )}

        {activeTab === 'bookshelf' && (
          <>
            <CurrentlyReading books={readingBooks} />
            <FavoriteBooks books={favoriteBooks} isOwnProfile={isOwnProfile} onReorder={reorderFavorites} />
            <RecentlyRead books={finishedBooks} />
            <TBRPreview books={tbrBooks} total={tbrBooks.length} />
          </>
        )}

        {activeTab === 'scribbles' && (
          <ScribblesSection scribbles={scribbles} authorName={profile.name} onToggleLike={toggleScribbleLike} />
        )}

        {activeTab === 'writing' && <WritingSection stories={publicStories} />}

        {activeTab === 'communities' && <CommunitiesSection communities={myCommunities} />}
      </main>

      {isOwnProfile && (
        <EditProfileModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          profile={profile}
          onSave={updateProfile}
          onUpdatePrivacy={updatePrivacy}
        />
      )}
    </div>
  );
}

export default function Profile() {
  return (
    <ProfileProvider>
      <ProfileContent />
    </ProfileProvider>
  );
}
